/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2019, Bulb Inc.  For more information, please email contact@hellobulb.com.
 */
angular.module('bulbApp.assetLibraryModule')
  .constant('GOOGLEPHOTO', {
    ALBUM_LIST: 'https://photoslibrary.googleapis.com/v1/albums?pageToken=',
    MEDIA_SEARCH_BY_ID: 'https://photoslibrary.googleapis.com/v1/mediaItems:search',
    MEDIA_TYPE: {
      PHOTO: ['PHOTO'],
      VIDEO: ['VIDEO'],
      ALL: ['PHOTO', 'VIDEO'],
    },
    PAGE_SIZE: 100,
  })
  .service('BulbAssetLibraryGooglePhotoService', [
    '$q',
    '$translate',
    'BulbBusyCoverService',
    'BulbErrorService',
    'BulbModalService',
    'google',
    'CONFIG',
    'GOOGLEPHOTO',
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    BulbAssetLibraryGooglePhotoService,
  ]);

function BulbAssetLibraryGooglePhotoService(
  $q,
  $translate,
  BulbBusyCoverService,
  BulbErrorService,
  BulbModalService,
  google,
  CONFIG,
  GOOGLEPHOTO,
): void {
  const that = this;

  // Scope for the Google Photos API
  const scope = 'https://www.googleapis.com/auth/photoslibrary.readonly';

  // Authentication token for the Google Photos API
  let oauthToken = null;

  // Flag to know whether google client has been loaded
  let googleApiLoaded = false;

  // Variable to be used as callback function
  let thisSelectHandler;

  // List of albums for the user
  this.albumList = [];

  // List of photos for the user
  this.mediaItemList = [];

  // Next page token
  this.nextPhotoPageToken = '';

  // flag for allowing multiple items selection.
  this.allowMultipleSelection = false;

  // flag for showing only videos
  this.showOnlyVideo = false;

  // flag for showing both videos and photos
  this.showAll = false;

  // Modal option to create google photos picker
  const modalOption = {
    titleCode: '',
    template: '<asset-library-google-photo></asset-library-google-photo>',
    windowClass: 'updated-modal-design bulb-assetlibrary-googlephoto',
    closeOnClick: true,
    showOkButton: false,
    showCancelButton: false,
  };

  // Check if google client and auth loaded.
  if (google.client && google.load) {
    google.load('auth2', () => {
      googleApiLoaded = true;
    });
  }

  /**
   * Handle response from google api during authorization process.
   * @param authResult result sent by google api.
   * @returns {*|promise}
   */
  const handleAuthResult = function handleAuthResult(authResult) {
    const deferred = $q.defer();
    if (authResult && !authResult.error) {
      /* jshint camelcase: false */
      oauthToken = authResult.access_token;
      deferred.resolve();
    } else {
      deferred.reject();
    }
    return deferred.promise;
  };

  /**
   * Request the google photo api to get the list of photos for the given albumId. If albumId is null,
   * all the photos for the given user will be returned.
   * @param pageToken next page token to get next records
   * @param albumId id of the album for which photos are to be fetched
   * @returns {promise} promise of data
   */
  function fetchPhotos(albumId) {
    const deferred = $q.defer();
    const requestObject = {
      path: GOOGLEPHOTO.MEDIA_SEARCH_BY_ID,
      method: 'POST',
      body: {},
    };

    // eslint-disable-next-line no-nested-ternary
    const mediaTypes = (!that.showAll)
      ? (that.showOnlyVideo ? GOOGLEPHOTO.MEDIA_TYPE.VIDEO : GOOGLEPHOTO.MEDIA_TYPE.PHOTO) : GOOGLEPHOTO.ALL;
    if (albumId) {
      requestObject.body = {
        albumId,
        pageSize: GOOGLEPHOTO.PAGE_SIZE,
        pageToken: that.nextPhotoPageToken,
      };
    } else {
      requestObject.body = {
        filters: {
          mediaTypeFilter: {
            mediaTypes,
          },
        },
        pageSize: GOOGLEPHOTO.PAGE_SIZE,
        pageToken: that.nextPhotoPageToken,
      };
    }

    google.client.request(requestObject).then((response) => {
      deferred.resolve(response.result);
    }, (error) => {
      BulbErrorService.showError(
        $translate.instant(
          that.showOnlyVideo
            ? 'assetlibrary_google_photos_video_import_error'
            : 'assetlibrary_google_photos_photo_import_error',
        ),
        error,
      );
      deferred.reject();
    });
    return deferred.promise;
  }

  /**
   *  Initializes the Google API client library and sets up sign-in state
   *  listeners.
   */
  const init = function init() {
    const deferred = $q.defer();

    if (!googleApiLoaded) {
      deferred.reject('Could not load the google client');
    }
    oauthToken = null;
    if (google.auth2.getAuthInstance()) {
      google.auth2.getAuthInstance().signOut();
    }
    google.auth2.authorize({
      client_id: CONFIG.google.client.id,
      scope,
      prompt: 'select_account',
    }, (authResult) => {
      handleAuthResult(authResult).then(deferred.resolve);
    });
    return deferred.promise;
  };

  /**
   * Get list of photos from Google Photo. Also, handles pagination for the returned records.
   * @param albumId id of album which photo will be fetched.
   * @returns {* | http.ClientRequest | ClientHttp2Stream}
   */
  function getMediaItems(albumId) {
    let mediaItemFilteredList;
    const deferred = $q.defer();
    fetchPhotos(albumId).then((response) => {
      if (response.mediaItems) {
        if (that.showAll) {
          mediaItemFilteredList = response.mediaItems;
        } else {
          mediaItemFilteredList = response.mediaItems
            .filter((mediaItem) => Object.keys(mediaItem.mediaMetadata)
              .includes(that.showOnlyVideo ? 'video' : 'photo'));
        }
        that.mediaItemList = that.mediaItemList.concat(mediaItemFilteredList);
      }
      that.nextPhotoPageToken = response.nextPageToken;
      deferred.resolve();
    }).catch(() => {
      deferred.reject();
    });
    return deferred.promise;
  }

  /**
   * Request the google photos api to get list of albums for the user.
   * @param pageToken next page token to get next records
   * @returns {promise} promise of data
   */
  function fetchAlbums(pageToken) {
    const deferred = $q.defer();
    google.client.request({
      path: GOOGLEPHOTO.ALBUM_LIST + pageToken,
      method: 'GET',
    }).then((response) => {
      if (response.result.albums) {
        that.albumList = that.albumList.concat(response.result.albums);
      }
      deferred.resolve(response.result.nextPageToken);
    }, (error) => {
      BulbErrorService.showError($translate.instant('assetlibrary_google_photos_album_import_error'), error);
      deferred.reject();
    });
    return deferred.promise;
  }

  /**
   * Retrieve list of album from google photo. Also, handles pagination for the returned records.
   * @returns {* | http.ClientRequest | ClientHttp2Stream}
   */
  async function getAlbumList() {
    const deferred = $q.defer();
    that.albumList = [];
    let nextPageToken = '';
    do {
      // eslint-disable-next-line no-await-in-loop, @typescript-eslint/no-loop-func
      await fetchAlbums(nextPageToken).then((data) => {
        nextPageToken = data;
        // eslint-disable-next-line @typescript-eslint/no-loop-func
      }, () => {
        deferred.reject();
      });
    } while (nextPageToken);
    deferred.resolve();
    return deferred.promise;
  }

  /**
   * Create google picker by open modal to show list of photos and albums.
   * @param selectHandler callback function.
   * @param albumId id of album.
   * @returns {*}
   */
  function createGooglePhotoPicker(selectHandler, albumId) {
    thisSelectHandler = selectHandler;
    const deferred = $q.defer();
    getAlbumList().then(() => {
      getMediaItems(albumId).then(() => {
        deferred.resolve();
        BulbModalService.open(modalOption);
      });
    }, () => {
      deferred.reject();
    });
    return deferred.promise;
  }

  /**
   * Initialize Google photo picker to select.
   * @param selectHandler - callback function for selected items
   * @param allowMultipleSelection - allow user to select multiple items. Default false
   * @param showOnlyVideo - filter to show videos only. Default false
   * @param showAll - filter to show all types of items. Default false
   */
  this.initializeGooglePhotoPicker = function initializeGooglePhotoPicker(
    selectHandler,
    allowMultipleSelection = false,
    showOnlyVideo = false,
    showAll = false,
  ) {
    that.allowMultipleSelection = allowMultipleSelection;
    that.showOnlyVideo = showOnlyVideo;
    that.showAll = showAll;
    return init().then(
      () => {
        that.albumList = [];
        that.mediaItemList = [];
        that.nextPhotoPageToken = null;
        BulbBusyCoverService.showBusyDuring(
          createGooglePhotoPicker(selectHandler, null),
        );
      },
    );
  };

  /**
   * Call callback function after photo selected.
   * @param mediaItemsId of photo which is selected.
   */
  this.selectPhotoHandler = function selectPhotoHandler(mediaItemsId) {
    thisSelectHandler(oauthToken, (that.allowMultipleSelection) ? mediaItemsId : mediaItemsId[0]);
  };

  /**
   * Returns the list of photos for the given albumId.
   * @param albumId id of the album for which photos are to be fetched
   * @returns {promise} promise of data
   */
  this.getAlbumPhotos = function getAlbumPhotos(albumId, refreshList) {
    const deferred = $q.defer();
    if (refreshList) {
      that.mediaItemList = [];
      that.nextPhotoPageToken = null;
    }

    getMediaItems(albumId).then((result) => {
      if (!result) {
        deferred.resolve(that.mediaItemList);
      } else {
        deferred.reject();
      }
    });
    return deferred.promise;
  };
}
